import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { exercisesApi, exerciseMapApi, messagesApi, usersApi } from '../../lib/api';
import Softphone from '../../components/ems/Softphone';
import ExerciseMap from '../../components/ems/ExerciseMap';
import PhoneDirectory from '../../components/ems/PhoneDirectory';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Phone, Map, BookUser, Loader2, ArrowLeftRight, Radio, Send, MessageSquare, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ParticipantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneTab, setPhoneTab] = useState('contacts');

  // Messages state
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [exerciseUsers, setExerciseUsers] = useState([]);
  const [msgCompose, setMsgCompose] = useState({ recipient_type: 'broadcast', subject: '', content: '', priority: 'normal', recipient_id: '', recipient_role: '' });
  const [msgSending, setMsgSending] = useState(false);
  const [msgViewItem, setMsgViewItem] = useState(null);
  const [msgReplyText, setMsgReplyText] = useState('');

  const hasMpAccess = user?.mp_roles?.length > 0;

  useEffect(() => { loadAssignedExercise(); }, []);

  const loadAssignedExercise = async () => {
    try {
      const exercises = await exercisesApi.getAll();
      const active = exercises.find(e => e.status === 'active') || exercises[0];
      if (active) setExercise(active);
    } catch (err) {
      console.error('Failed to load exercises:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = useCallback(async () => {
    if (!exercise) return;
    setMessagesLoading(true);
    try {
      const data = await messagesApi.getAll(exercise.exercise_id);
      setMessages(data);
    } catch { /* ignore */ }
    finally { setMessagesLoading(false); }
    // Load exercise users separately (may fail for participants — that's OK)
    try {
      const usersList = await usersApi.getAll();
      setExerciseUsers(usersList);
    } catch { /* participants may not have access to user list */ }
  }, [exercise]);

  useEffect(() => { if (exercise) loadMessages(); }, [exercise, loadMessages]);

  // Poll for new messages every 10 seconds
  const lastMsgCountRef = React.useRef(0);
  useEffect(() => {
    if (!exercise) return;
    const poll = async () => {
      try {
        const data = await messagesApi.getAll(exercise.exercise_id);
        if (data.length > lastMsgCountRef.current && lastMsgCountRef.current > 0) {
          const newCount = data.length - lastMsgCountRef.current;
          const newest = data[0];
          if (newest && newest.sender_id !== user?.user_id) {
            toast.info(`New message from ${newest.sender_name}: ${newest.subject}`, { duration: 5000 });
          }
        }
        lastMsgCountRef.current = data.length;
        setMessages(data);
      } catch { /* silent */ }
    };
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [exercise, user?.user_id]);

  const handleSendMessage = async () => {
    if (!msgCompose.subject.trim() || !msgCompose.content.trim()) { toast.error('Subject and content required'); return; }
    setMsgSending(true);
    try {
      const payload = {
        exercise_id: exercise.exercise_id,
        subject: msgCompose.subject,
        content: msgCompose.content,
        priority: msgCompose.priority,
        is_group: msgCompose.recipient_type !== 'individual',
      };
      if (msgCompose.recipient_type === 'individual' && msgCompose.recipient_id) {
        payload.recipient_id = msgCompose.recipient_id;
      } else if (msgCompose.recipient_type === 'role' && msgCompose.recipient_role) {
        payload.recipient_role = msgCompose.recipient_role;
      }
      await messagesApi.send(payload);
      toast.success('Message sent');
      setMsgCompose({ recipient_type: 'broadcast', subject: '', content: '', priority: 'normal', recipient_id: '', recipient_role: '' });
      loadMessages();
    } catch (err) { toast.error(err.message || 'Failed to send'); }
    finally { setMsgSending(false); }
  };

  const handleReplyMessage = async (messageId) => {
    if (!msgReplyText.trim()) return;
    try {
      await messagesApi.reply(messageId, msgReplyText);
      toast.success('Reply sent');
      setMsgReplyText('');
      setMsgViewItem(null);
      loadMessages();
    } catch (err) { toast.error(err.message || 'Failed to reply'); }
  };

  if (loading) {
    return (
      <DashboardLayout title="COMMAND CENTER">
        <div className="flex items-center justify-center h-[70vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!exercise) {
    return (
      <DashboardLayout title="COMMAND CENTER">
        <div className="flex items-center justify-center h-[70vh] text-muted-foreground">
          <div className="text-center">
            <Map className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No exercise assigned</p>
            <p className="text-sm mt-1">Contact your administrator to be assigned to an exercise.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="COMMAND CENTER">
      {/* Exercise header */}
      <div className="flex items-center justify-between mb-3" data-testid="participant-header">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold tracking-tight">{exercise.name}</h2>
          <Badge variant={exercise.status === 'active' ? 'default' : 'secondary'} className="capitalize text-[10px]">
            {exercise.status}
          </Badge>
        </div>
        {hasMpAccess && (
          <button
            onClick={() => navigate('/mp/dashboard')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-red-500/30 bg-red-500/5 text-red-400 text-xs font-medium hover:bg-red-500/10 transition-colors"
            data-testid="switch-to-mp-btn"
          >
            <Radio className="w-3.5 h-3.5" />
            Switch to Media Portal
            <ArrowLeftRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* 3-column layout: Phone | Map | Messages */}
      <div className="grid grid-cols-12 gap-3" style={{ height: 'calc(100vh - 140px)' }} data-testid="participant-dashboard">

        {/* Left: Phone + Exercise Contacts */}
        <div className="col-span-3 flex flex-col gap-0 overflow-hidden">
          <div className="border rounded-lg bg-card flex-1 overflow-hidden flex flex-col">
            <div className="flex items-center border-b bg-muted/30">
              <button
                onClick={() => setPhoneTab('phone')}
                className={`flex-1 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors ${
                  phoneTab === 'phone' ? 'text-primary bg-primary/5 border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="phone-subtab-phone"
              >
                <Phone className="w-3.5 h-3.5" /> Phone
              </button>
              <button
                onClick={() => setPhoneTab('contacts')}
                className={`flex-1 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors ${
                  phoneTab === 'contacts' ? 'text-primary bg-primary/5 border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="phone-subtab-contacts"
              >
                <BookUser className="w-3.5 h-3.5" /> Contacts
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {phoneTab === 'phone' ? (
                <Softphone exerciseId={exercise.exercise_id} embedded />
              ) : (
                <PhoneDirectory exerciseId={exercise.exercise_id} compact />
              )}
            </div>
          </div>
        </div>

        {/* Center: Exercise Map */}
        <div className="col-span-6 flex flex-col gap-0 overflow-hidden">
          <div className="border rounded-lg overflow-hidden bg-card flex-1">
            <ExerciseMap
              exerciseId={exercise.exercise_id}
              canEdit={false}
              mapApi={exerciseMapApi}
            />
          </div>
        </div>

        {/* Right: Messages Panel */}
        <div className="col-span-3 flex flex-col gap-0 overflow-hidden">
          <div className="border rounded-lg bg-card flex-1 overflow-hidden flex flex-col" data-testid="messages-panel">
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-muted/30">
              <MessageSquare className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">Messages</span>
              {messages.filter(m => !m.is_read && m.sender_id !== user?.user_id).length > 0 && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold animate-pulse tabular-nums">
                  {messages.filter(m => !m.is_read && m.sender_id !== user?.user_id).length}
                </span>
              )}
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 tabular-nums">
                {messages.length}
              </span>
            </div>

            {/* Messages content */}
            <ScrollArea className="flex-1">
              <div className="p-3">
                {msgViewItem ? (
                  /* Detail / reply view */
                  <div className="space-y-3" data-testid="message-detail-view">
                    <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => setMsgViewItem(null)}>
                      <ChevronLeft className="w-3 h-3" /> Back
                    </button>
                    <div className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold">{msgViewItem.subject}</p>
                        <Badge variant="outline" className={`text-[9px] capitalize ${msgViewItem.priority === 'urgent' ? 'text-red-500 border-red-300' : msgViewItem.priority === 'high' ? 'text-orange-500 border-orange-300' : ''}`}>{msgViewItem.priority}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground">From: {msgViewItem.sender_name || msgViewItem.sender_id}</p>
                      <p className="text-xs whitespace-pre-wrap border-t pt-2">{msgViewItem.content}</p>
                    </div>
                    {msgViewItem.replies?.map((r, i) => (
                      <div key={i} className="ml-3 border-l-2 pl-2 py-1">
                        <p className="text-[10px] font-medium">{r.sender_name || r.sender_id}</p>
                        <p className="text-xs mt-0.5">{r.content}</p>
                      </div>
                    ))}
                    <div className="flex gap-1.5">
                      <Input
                        value={msgReplyText}
                        onChange={(e) => setMsgReplyText(e.target.value)}
                        placeholder="Reply..."
                        className="h-7 text-xs flex-1"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleReplyMessage(msgViewItem.message_id); }}
                        data-testid="message-reply-input"
                      />
                      <Button size="sm" className="h-7 px-2" onClick={() => handleReplyMessage(msgViewItem.message_id)} disabled={!msgReplyText.trim()} data-testid="message-reply-btn">
                        <Send className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Compose + list */
                  <div className="space-y-3">
                    {/* Compose */}
                    <div className="border rounded-lg p-2.5 space-y-2 bg-muted/20" data-testid="message-compose">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">New Message</p>
                      <Select value={msgCompose.recipient_type} onValueChange={(v) => setMsgCompose({...msgCompose, recipient_type: v, recipient_id: '', recipient_role: ''})}>
                        <SelectTrigger className="h-7 text-xs" data-testid="msg-send-to"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="broadcast">Everyone in Exercise</SelectItem>
                          <SelectItem value="role">Role Group</SelectItem>
                          <SelectItem value="individual">Specific Person</SelectItem>
                        </SelectContent>
                      </Select>
                      {msgCompose.recipient_type === 'role' && (
                        <Select value={msgCompose.recipient_role} onValueChange={(v) => setMsgCompose({...msgCompose, recipient_role: v})}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select role..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="super_admin">Admins</SelectItem>
                            <SelectItem value="simcel">Simcells</SelectItem>
                            <SelectItem value="participant">Participants</SelectItem>
                            <SelectItem value="observer">Observers</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {msgCompose.recipient_type === 'individual' && (
                        <Select value={msgCompose.recipient_id} onValueChange={(v) => setMsgCompose({...msgCompose, recipient_id: v})}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select person..." /></SelectTrigger>
                          <SelectContent>
                            {exerciseUsers.filter(u => u.user_id !== user?.user_id).map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.roles?.join(', ')})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        value={msgCompose.subject}
                        onChange={(e) => setMsgCompose({...msgCompose, subject: e.target.value})}
                        placeholder="Subject"
                        className="h-7 text-xs"
                        data-testid="message-subject-input"
                      />
                      <textarea
                        className="w-full min-h-[50px] border rounded-md p-2 text-xs bg-background resize-y"
                        placeholder="Message content..."
                        value={msgCompose.content}
                        onChange={(e) => setMsgCompose({...msgCompose, content: e.target.value})}
                        data-testid="message-content-input"
                      />
                      <div className="flex items-center justify-between">
                        <Select value={msgCompose.priority} onValueChange={(v) => setMsgCompose({...msgCompose, priority: v})}>
                          <SelectTrigger className="h-6 text-[10px] w-20"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" className="h-7 text-xs" onClick={handleSendMessage} disabled={msgSending || !msgCompose.subject.trim() || !msgCompose.content.trim()} data-testid="message-send-btn">
                          <Send className="w-3 h-3 mr-1" />{msgSending ? '...' : 'Send'}
                        </Button>
                      </div>
                    </div>

                    {/* Messages list */}
                    <div className="space-y-1.5">
                      {messagesLoading ? (
                        <p className="text-xs text-muted-foreground text-center py-4">Loading...</p>
                      ) : messages.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-6">No messages yet</p>
                      ) : (
                        messages.map((m) => {
                          const isUnread = !m.is_read && m.sender_id !== user?.user_id;
                          return (
                          <button
                            key={m.message_id}
                            className={`w-full text-left p-2 rounded-lg border hover:bg-muted/50 transition-colors space-y-0.5 ${isUnread ? 'border-primary/40 bg-primary/5' : ''}`}
                            onClick={() => setMsgViewItem(m)}
                            data-testid={`message-item-${m.message_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <p className={`text-[11px] truncate flex-1 ${isUnread ? 'font-bold' : 'font-semibold'}`}>{m.subject}</p>
                              <div className="flex items-center gap-1 ml-1 flex-shrink-0">
                                {isUnread && <span className="text-[8px] px-1 py-0.5 rounded bg-primary text-primary-foreground font-bold">NEW</span>}
                                <Badge variant="outline" className={`text-[8px] ${m.priority === 'urgent' ? 'text-red-500 border-red-300' : m.priority === 'high' ? 'text-orange-500 border-orange-300' : ''}`}>{m.priority}</Badge>
                              </div>
                            </div>
                            <p className="text-[10px] text-muted-foreground truncate">{m.content}</p>
                            <div className="flex items-center justify-between">
                              <p className="text-[10px] text-muted-foreground">{m.sender_name || m.sender_id}</p>
                              <p className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                            {m.replies?.length > 0 && (
                              <p className="text-[10px] text-primary">{m.replies.length} {m.replies.length === 1 ? 'reply' : 'replies'}</p>
                            )}
                          </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
